import React, { useState, useEffect } from 'react';
import { Users, Building2, FileText, TrendingUp, Network, Shield, Stethoscope, Crown, Activity, Calendar, BarChart3, PieChart } from 'lucide-react';
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";
import { NewNetworkAxiosInstance } from "../../Network/NewNetworkAxiosInstance";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import { InsuranceAxiosInstance } from "../../Network/InsuranceAxiosInstance";
import { DoctorAxiosInstance } from "../../Network/DoctorAxiosInstance";
import { VipAxiosInstance } from "../../Network/VipAxiosInstance";
import { useNavigate } from "react-router-dom";
import { useAuth } from "Pages/PrivateRoute/AuthContext";

const Dashboard = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const [loading, setLoading] = useState(true);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [stats, setStats] = useState({
    patients: 0, activePatients: 0, inactivePatients: 0,
    providers: 0, activeProviders: 0, inactiveProviders: 0,
    networks: 0, activeNetworks: 0, inactiveNetworks: 0,
    policies: 0, activePolicies: 0, inactivePolicies: 0,
    insurancePlans: 0, activeInsurancePlans: 0, inactiveInsurancePlans: 0,
    doctors: 0, activeDoctors: 0, inactiveDoctors: 0,
    vipLists: 0, activeVipLists: 0, inactiveVipLists: 0,
  });

  useEffect(() => {
    fetchAllData();
    
    const dateInterval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    return () => clearInterval(dateInterval);
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const patientsRes = await PatientRelationAxiosInstance.get("getpatient");
      const patientsData = patientsRes.data.result || [];
      const activePatients = patientsData.filter((p: any) => p.status === true || p.status === "Active").length;

      const providersRes = await providerAxiosInstance.get("getProvider");
      const providersData = providersRes.data.result || [];
      const activeProviders = providersData.filter((p: any) => p.status === true || p.status === "Active").length;

      const networksRes = await NewNetworkAxiosInstance.get("getNetwork");
      const networksData = networksRes.data.result || [];
      const activeNetworks = networksData.filter((n: any) => n.status === true || n.status === "Active").length;

      const policiesRes = await PolicyAxiosInstance.get("getpolicy");
      const policiesData = policiesRes.data.result || [];
      const activePolicies = policiesData.filter((p: any) => p.status === true || p.status === "Active").length;

      const plansRes = await InsuranceAxiosInstance.get("getInsurancePlan");
      const plansData = plansRes.data.result || [];
      const activePlans = plansData.filter((p: any) => p.status === true || p.status === "Active").length;

      const doctorsRes = await DoctorAxiosInstance.get("getdoctor");
      const doctorsData = doctorsRes.data.result || [];
      const activeDoctors = doctorsData.filter((d: any) => d.status === true || d.status === "Active").length;

      const vipRes = await VipAxiosInstance.get("getVip");
      const vipData = vipRes.data.result || [];
      const activeVip = vipData.filter((v: any) => v.status === true || v.status === "Active").length;

      setStats({
        patients: patientsData.length,
        activePatients: activePatients,
        inactivePatients: patientsData.length - activePatients,
        providers: providersData.length,
        activeProviders: activeProviders,
        inactiveProviders: providersData.length - activeProviders,
        networks: networksData.length,
        activeNetworks: activeNetworks,
        inactiveNetworks: networksData.length - activeNetworks,
        policies: policiesData.length,
        activePolicies: activePolicies,
        inactivePolicies: policiesData.length - activePolicies,
        insurancePlans: plansData.length,
        activeInsurancePlans: activePlans,
        inactiveInsurancePlans: plansData.length - activePlans,
        doctors: doctorsData.length,
        activeDoctors: activeDoctors,
        inactiveDoctors: doctorsData.length - activeDoctors,
        vipLists: vipData.length,
        activeVipLists: activeVip,
        inactiveVipLists: vipData.length - activeVip,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setLoading(false);
    }
  };

  const calculatePercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(0) : '0';
  };

  const totalEntities = stats.patients + stats.providers + stats.networks + stats.policies + stats.insurancePlans + stats.doctors + stats.vipLists;
  const totalActive = stats.activePatients + stats.activeProviders + stats.activeNetworks + stats.activePolicies + stats.activeInsurancePlans + stats.activeDoctors + stats.activeVipLists;
  const totalInactive = totalEntities - totalActive;

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-title">
            <div className="header-icon">
              <TrendingUp size={36} />
            </div>
            <div>
              <h1>LEEN Healthcare Dashboard</h1>
              <p>Real-time System Statistics & Analytics</p>
            </div>
          </div>
          <div className="date-display">
            <Calendar size={18} />
            <span>{currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>
      </div>

      {/* Summary Overview */}
      <div className="summary-section">
        <div className="summary-card total">
          <div className="summary-icon"><BarChart3 size={28} /></div>
          <div className="summary-info">
            <h3>{totalEntities}</h3>
            <p>Total Entities</p>
          </div>
        </div>
        <div className="summary-card active">
          <div className="summary-icon"><Activity size={28} /></div>
          <div className="summary-info">
            <h3>{totalActive}</h3>
            <p>Active Entities</p>
          </div>
        </div>
        <div className="summary-card inactive">
          <div className="summary-icon"><PieChart size={28} /></div>
          <div className="summary-info">
            <h3>{totalInactive}</h3>
            <p>Inactive Entities</p>
          </div>
        </div>
        <div className="summary-card percentage">
          <div className="summary-icon"><TrendingUp size={28} /></div>
          <div className="summary-info">
            <h3>{calculatePercentage(totalActive, totalEntities)}%</h3>
            <p>Active Rate</p>
          </div>
        </div>
      </div>

      {/* Quick Stats Cards - 5 Columns as Requested */}
      <div className="quick-stats">
        {[
          { icon: Users, count: stats.patients, label: 'Patients', active: stats.activePatients, inactive: stats.inactivePatients, color: 'blue' },
          { icon: Stethoscope, count: stats.providers, label: 'Providers', active: stats.activeProviders, inactive: stats.inactiveProviders, color: 'purple' },
          { icon: Activity, count: stats.doctors, label: 'Doctors', active: stats.activeDoctors, inactive: stats.inactiveDoctors, color: 'cyan' },
          { icon: Network, count: stats.networks, label: 'Networks', active: stats.activeNetworks, inactive: stats.inactiveNetworks, color: 'pink' },
          { icon: Shield, count: stats.policies, label: 'Policies', active: stats.activePolicies, inactive: stats.inactivePolicies, color: 'orange' },
          { icon: FileText, count: stats.insurancePlans, label: 'Insurance Plans', active: stats.activeInsurancePlans, inactive: stats.inactiveInsurancePlans, color: 'green' },
          { icon: Crown, count: stats.vipLists, label: 'VIP Lists', active: stats.activeVipLists, inactive: stats.inactiveVipLists, color: 'gold' },
        ].map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-icon">
              <stat.icon size={24} />
            </div>
            <div className="stat-info">
              <h4>{stat.count}</h4>
              <p>{stat.label}</p>
              <div className="progress-mini">
                <div className="progress-mini-bar">
                  <div 
                    className="progress-mini-fill" 
                    style={{ width: `${calculatePercentage(stat.active, stat.count)}%` }}
                  ></div>
                </div>
                <span className="progress-mini-text">{calculatePercentage(stat.active, stat.count)}% Active</span>
              </div>
              <div className="stat-badges">
                <span className="active">✓ {stat.active}</span>
                <span className="inactive">✕ {stat.inactive}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style >{`
        .dashboard-container {
          min-height: 100vh;
          padding: 2rem;
          background: #f0f4f8;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 80vh;
          color: #1f2937;
        }

        .loader {
          border: 5px solid rgba(102, 126, 234, 0.2);
          border-top: 5px solid #667eea;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .dashboard-header {
          margin-bottom: 1.5rem;
          background: rgba(255, 255, 255, 0.95);
          padding: 1.25rem;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          backdrop-filter: blur(10px);
          margin-top: 4rem;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-title {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          background: linear-gradient(135deg, #011934, #011934);
          color: white;
          padding: 0.75rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .header-title h1 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
          color: #1f2937;
        }

        .header-title p {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0.25rem 0 0 0;
        }

        .date-display {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 1.25rem;
          background: linear-gradient(135deg, #011934, #011934);
          color: white;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .summary-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .summary-card {
          background: white;
          padding: 1.25rem;
          border-radius: 14px;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease;
        }

        .summary-card:hover { transform: translateY(-5px); }

        .summary-card.total { border-left: 4px solid #3b82f6; }
        .summary-card.active { border-left: 4px solid #10b981; }
        .summary-card.inactive { border-left: 4px solid #ef4444; }
        .summary-card.percentage { border-left: 4px solid #f59e0b; }

        .summary-icon {
          padding: 0.65rem;
          border-radius: 10px;
          background: #f3f4f6;
        }

        .summary-card.total .summary-icon { background: #dbeafe; color: #3b82f6; }
        .summary-card.active .summary-icon { background: #d1fae5; color: #10b981; }
        .summary-card.inactive .summary-icon { background: #fee2e2; color: #ef4444; }
        .summary-card.percentage .summary-icon { background: #fef3c7; color: #f59e0b; }

        .summary-info h3 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 700;
          color: #1f2937;
        }

        .summary-info p {
          margin: 0;
          font-size: 0.8rem;
          color: #6b7280;
        }

        /* 5 أعمدة بالضبط كما طلب المدير */
        .quick-stats {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 1.5rem;
          padding: 0.5rem 0;
          overflow-x: auto;
        }

        .stat-card {
          background: white;
          padding: 1.25rem;
          border-radius: 14px;
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          min-width: 240px;
        }

        .stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
        }

        .stat-icon {
          padding: 0.85rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .stat-card.blue .stat-icon { background: #dbeafe; color: #3b82f6; }
        .stat-card.purple .stat-icon { background: #ede9fe; color: #8b5cf6; }
        .stat-card.cyan .stat-icon { background: #cffafe; color: #06b6d4; }
        .stat-card.pink .stat-icon { background: #fce7f3; color: #ec4899; }
        .stat-card.orange .stat-icon { background: #fef3c7; color: #f59e0b; }
        .stat-card.green .stat-icon { background: #d1fae5; color: #10b981; }
        .stat-card.gold .stat-icon { background: #fef9c3; color: #eab308; }

        .stat-info h4 {
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
        }

        .stat-info p {
          margin: 0 0 0.75rem 0;
          font-size: 0.9rem;
          color: #4b5563;
          font-weight: 600;
        }

        .progress-mini-bar {
          height: 6px;
          background: #e5e7eb;
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 0.4rem;
        }

        .progress-mini-fill {
          height: 100%;
          background: linear-gradient(90deg, #10b981, #059669);
          transition: width 0.6s ease;
        }

        .progress-mini-text {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 600;
        }

        .stat-badges {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.75rem;
        }

        .stat-badges span {
          padding: 0.35rem 0.75rem;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .stat-badges .active {
          background: #d1fae5;
          color: #065f46;
        }

        .stat-badges .inactive {
          background: #fee2e2;
          color: #991b1b;
        }

        /* Responsive - تكييف ذكي للشاشات الأصغر */
        @media (max-width: 1600px) {
          .quick-stats { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 1280px) {
          .quick-stats { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 900px) {
          .quick-stats { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 600px) {
          .quick-stats { grid-template-columns: 1fr; }
          .dashboard-container { padding: 1rem; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
